import { useCallback } from 'react';
import { Link } from 'react-router-dom';

import {
  buscarMeusEventos,
  buscarMeusPedidos,
  buscarMinhaAssinatura,
} from '@/api/conta';
import { useRequisicao } from '@/hooks/useRequisicao';
import { formatarDataHora, formatarDataLonga } from '@/lib/formato';

import { Carregando, Cartao, ErroCarregamento, Secao, Status } from './ContaUI';

async function carregarResumo() {
  const [eventos, assinatura, pedidos] = await Promise.all([
    buscarMeusEventos(),
    buscarMinhaAssinatura(),
    buscarMeusPedidos(),
  ]);
  return { eventos: eventos ?? [], assinatura, pedidos: pedidos ?? [] };
}

export default function VisaoGeral() {
  const buscar = useCallback(carregarResumo, []);
  const { dados, carregando, erro, recarregar } = useRequisicao(buscar);

  if (carregando) {
    return (
      <Secao titulo="Visão geral">
        <Carregando linhas={2} />
      </Secao>
    );
  }

  if (erro) {
    return (
      <Secao titulo="Visão geral">
        <ErroCarregamento onTentarNovamente={recarregar} />
      </Secao>
    );
  }

  const { eventos, assinatura, pedidos } = dados;

  const agora = Date.now();
  const proximo = eventos
    .filter((i) => {
      const data = i.eventDay?.startDate;
      return data && new Date(data).getTime() >= agora;
    })
    .sort(
      (a, b) => new Date(a.eventDay.startDate) - new Date(b.eventDay.startDate),
    )[0];

  return (
    <div className="space-y-10">
      <Secao titulo="Próximo encontro">
        {proximo ? (
          <Cartao>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="font-display text-ink text-xl">
                  {proximo.eventDay?.event?.title ?? 'Encontro'}
                </h3>
                <p className="text-ink-soft mt-1 text-sm">
                  {formatarDataHora(proximo.eventDay?.startDate)}
                </p>
                {proximo.eventDay?.event?.location && (
                  <p className="text-ink-soft/60 mt-1 text-sm">
                    {proximo.eventDay.event.location}
                  </p>
                )}
              </div>
              <Status valor={proximo.status} />
            </div>
          </Cartao>
        ) : (
          <Cartao>
            <p className="text-ink-soft/70 text-sm">
              Nenhum encontro marcado no momento.{' '}
              <Link
                to="/"
                className="text-wine hover:text-wine-deep font-semibold underline-offset-4 hover:underline"
              >
                Ver o que vem por aí
              </Link>
            </p>
          </Cartao>
        )}
      </Secao>

      <Secao titulo="Resumo">
        <div className="grid gap-3 sm:grid-cols-3">
          <Link to="/minha-conta/eventos" className="group">
            <Cartao className="group-hover:border-wine/40 h-full transition-colors">
              <p className="font-display text-ink text-3xl">{eventos.length}</p>
              <p className="text-ink-soft/70 mt-1 text-sm">
                inscrição(ões) em encontros
              </p>
            </Cartao>
          </Link>

          <Link to="/minha-conta/pedidos" className="group">
            <Cartao className="group-hover:border-wine/40 h-full transition-colors">
              <p className="font-display text-ink text-3xl">{pedidos.length}</p>
              <p className="text-ink-soft/70 mt-1 text-sm">pedido(s) na loja</p>
            </Cartao>
          </Link>

          <Link to="/minha-conta/assinatura" className="group">
            <Cartao className="group-hover:border-wine/40 h-full transition-colors">
              <p className="text-ink-soft/50 text-xs tracking-[0.12em] uppercase">
                Lily Pass
              </p>
              {assinatura ? (
                <>
                  <div className="mt-2">
                    <Status valor={assinatura.status} />
                  </div>
                  <p className="text-ink-soft/60 mt-2 text-xs">
                    até {formatarDataLonga(assinatura.endDate)}
                  </p>
                </>
              ) : (
                <p className="text-ink-soft/70 mt-2 text-sm">
                  sem assinatura ativa
                </p>
              )}
            </Cartao>
          </Link>
        </div>
      </Secao>
    </div>
  );
}
